import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Shield, Loader2, Eye, EyeOff, Landmark, LockKeyhole, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { toast } from "sonner";
import { cn } from "../lib/utils";

export const Login = () => {
  const navigate = useNavigate();
  const { login, verifyOTP } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    accountNumber: "",
    password: "",
    email: "",
    otp: "",
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const result = await login(formData.accountNumber, formData.password);
      console.log(result)
      setFormData((prev) => ({ ...prev, email: result.email }));
      if(formData.accountNumber==='admin'){
      
      toast.success("Your OTP is: " +result.otp, {duration: 15000});

      }
      setStep(2);
    } catch (err) {
      console.log(err.response)
      setError(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await verifyOTP(formData.email, formData.otp);
      toast.success("Login successful!");
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-0 md:p-6 font-sans">
      <div className="w-full max-w-[450px] h-screen md:h-auto md:min-h-[700px] bg-white md:rounded-[3rem] shadow-2xl overflow-hidden flex flex-col relative border border-slate-200/60">
        
        {/* Background Decoration */}
        <div className="absolute -top-12 -left-12 pointer-events-none">
          <Landmark className="w-64 h-64 text-blue-600 opacity-[0.03] -rotate-12" />
        </div>

        {/* Header */}
        <header className="px-10 pt-16 pb-8 z-10 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-[1.5rem] bg-slate-900 flex items-center justify-center shadow-xl shadow-slate-200">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight leading-tight">
            {step === 1 ? "Secure Access" : "OTP Verification"}
          </h1>
          <p className="text-slate-400 text-sm mt-2">
            {step === 1 
              ? "Welcome back to your digital vault" 
              : `A 6-digit code was sent to ${formData.email}`}
          </p>
              {error && (
            <Alert variant="destructive" className="mt-3 rounded-2xl bg-red-50 border-none text-red-600 animate-in fade-in slide-in-from-top-2">
              <AlertDescription className="text-xs font-bold flex items-center gap-2">
                <LockKeyhole size={14} /> {error}
              </AlertDescription>
            </Alert>
          )}

        </header>

        <div className="flex-1 px-10 z-10">
      
          {step === 1 ? (
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-bold text-slate-500 ml-1 uppercase tracking-wider">Account Number</Label>
                <Input
                  className="h-14 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-lg font-medium placeholder:text-slate-300 focus-visible:ring-blue-600/20"
                  placeholder="0000000000"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData((prev) => ({ ...prev, accountNumber: e.target.value }))}
                  required
                  data-testid="account-number-input"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex justify-between items-center px-1">
                  <Label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Password</Label>
                  <Link to="/forgot-password" size="sm" className="text-[11px] font-bold text-blue-600 hover:underline" data-testid="forgot-password-link">
                    Forgot?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    className="h-14 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-lg placeholder:text-slate-300"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-14 bg-slate-900 text-white rounded-2xl font-bold shadow-xl hover:bg-slate-800 hover:scale-[1.01] active:scale-95 transition-all mt-4"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? <Loader2 className="animate-spin" /> : "Authorize Access"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-8 animate-in fade-in slide-in-from-right-4">
              <div className="flex justify-center py-4">
                <InputOTP
                  maxLength={6}
                  value={formData.otp}
                  onChange={(value) => setFormData((prev) => ({ ...prev, otp: value }))}
                  data-testid="otp-input"
                >
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <InputOTPSlot 
                        key={index} 
                        index={index} 
                        className="w-12 h-14 rounded-xl border-slate-200 bg-slate-50 text-slate-900 font-bold text-xl"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <div className="space-y-4">
                <Button
                  type="submit"
                  className="w-full h-14 bg-blue-600 text-white rounded-2xl font-bold shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all"
                  disabled={loading || formData.otp.length !== 6}
                  data-testid="verify-otp-btn"
                >
                  {loading ? <Loader2 className="animate-spin" /> : "Verify"}
                </Button>
                
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full h-12 text-slate-500 font-bold hover:bg-slate-50 rounded-xl"
                  onClick={() => setStep(1)}
                >
                  <ArrowLeft size={16} className="mr-2" /> Back to Account
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <footer className="px-10 py-10 mt-auto bg-white border-t border-slate-50 z-20">
          <div className="flex flex-col items-center gap-4">
             <p className="text-sm text-slate-500 font-medium">
               New to the platform? <Link to="/signup" className="text-blue-600 font-bold ml-1 hover:underline" data-testid="signup-link">Create Account</Link>
             </p>
             <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-full">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                <span className="text-[12px] font-bold text-slate-500 uppercase tracking-tight">System Secure & Online</span>
             </div>
          </div>
        </footer>
      </div>
    </div>
  );
};